/**
 * DevLoadResults — POC dev-only floating trigger.
 *
 * On a local-only run the live rate backend (and the origin/destination
 * autocomplete that drives the search form) is unreachable, so the results
 * screen — and the "Create RFQ" entry point mounted on the rate cards — can't be
 * reached by hand. These buttons render the bundled sample fixtures straight into
 * the results view (via loadLocalRatesForDev), so the whole Create-RFQ flow can
 * be exercised offline for each shipment type:
 *   FCL           → container-quantity creator
 *   LCL           → weight + CBM creator
 *   Air           → weight + CBM creator, AIR mode
 *   FCL · partial → an FCL card whose delivery charge is "not available", so the
 *                   in-card "Create RFQ" partial flow (pin freight, RFQ the gap)
 *                   is reachable once Delivery is checked.
 *
 * It also carries a "login / logout" toggle: the RFQ feature is gated to
 * logged-in users (#75 c11), and a local-only run has no real login, so this
 * seeds / clears a fake USER_DETAILS so both states — guest (no RFQ UI) and
 * logged-in (RFQ banner + in-card button + per-leg radio) — can be verified.
 *
 * Renders nothing unless REACT_APP_USE_LOCAL_RATES === 'yes', so production and
 * normal dev builds are completely unaffected. Mounted once in App.js.
 */
import React from 'react';
import { connect } from 'react-redux';
import { loadLocalRatesForDev, devSetLoggedInUser } from '../../actions/form_actions';
import { isDevENV } from '../../util/isDevENV';
import { isLoggedInUser } from '../../util/isLoggedInUser';

const VARIANTS = [
  { key: 'fcl', label: 'FCL' },
  { key: 'lcl', label: 'LCL' },
  { key: 'air', label: 'Air' },
  { key: 'partial', label: 'FCL · partial' },
  { key: 'estimate', label: 'FCL · estimate' },
];

const btnStyle = {
  padding: '6px 10px',
  background: '#2a2a2a',
  color: '#fff',
  border: '1px solid #555',
  borderRadius: 4,
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
};

const DevLoadResults = ({ loadLocalRatesForDev, devSetLoggedInUser, userDetails }) => {
  // Belt-and-braces: hide unless we're in a dev build AND the fixture flag is on,
  // so a stray REACT_APP_USE_LOCAL_RATES=yes can never surface this in production.
  if (!isDevENV() || process.env.REACT_APP_USE_LOCAL_RATES !== 'yes') return null;

  const loggedIn = isLoggedInUser(userDetails);

  return (
    <div
      style={{
        position: 'fixed',
        right: 16,
        bottom: 16,
        zIndex: 9999,
        display: 'flex',
        gap: 6,
        alignItems: 'center',
        background: '#1f1f1f',
        border: '1px solid #f5222d',
        borderRadius: 6,
        padding: '8px 10px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
      }}
    >
      <span style={{ color: '#fff', fontSize: 12, fontWeight: 600, marginRight: 4 }}>
        DEV · load sample:
      </span>
      {VARIANTS.map((v) => (
        <button
          key={v.key}
          type="button"
          onClick={() => loadLocalRatesForDev(v.key)}
          title={`Load a sample ${v.label} lane (Shanghai → Toronto) so you can reach the results + Create RFQ offline`}
          style={btnStyle}
        >
          {v.label}
        </button>
      ))}
      {/* RFQ is logged-in-only (#75 c11): toggle a fake login to show/hide the
          RFQ banner, in-card button and per-leg radio offline. */}
      <span style={{ width: 1, alignSelf: 'stretch', background: '#555', margin: '0 2px' }} />
      <button
        type="button"
        onClick={() => devSetLoggedInUser(!loggedIn)}
        title={
          loggedIn
            ? 'Logged in as Dev User — RFQ UI is visible. Click to log out (guest) and confirm it hides.'
            : 'Guest — RFQ UI is hidden. Click to log in (Dev User) and confirm it appears.'
        }
        style={{
          ...btnStyle,
          background: loggedIn ? '#135200' : '#2a2a2a',
          borderColor: loggedIn ? '#52c41a' : '#555',
        }}
      >
        {loggedIn ? '👤 logged in' : '🚪 guest'}
      </button>
    </div>
  );
};

const mapStateToProps = (state) => ({
  userDetails: state.route.USER_DETAILS,
});

export default connect(mapStateToProps, { loadLocalRatesForDev, devSetLoggedInUser })(
  DevLoadResults
);
